import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarPlus, Clock, Dumbbell, Flame, HeartPulse, Layers, LineChart, Library, ListChecks, Play, RotateCcw, User as UserIcon, Watch } from 'lucide-react';
import { client, workoutExecution } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useCapabilities } from '../contexts/CapabilitiesContext';
import InstallHint from '../components/InstallHint';
import { estimateMinutes, plural } from '../components/WorkoutPlayer/playerLogic';
import { legacyHomeTarget } from '../utils/legacyRoutes';
import '../styles/ui.css';
import './ClientHomePage.css';

// Dashboard — a página inicial de quem treina e de quem treina outros: responde primeiro a "o que tenho para fazer agora?". Tudo vem de um único endpoint agregado;
// as secções que dependem de um módulo desligado, ou sem dados, simplesmente não aparecem.
const QUICK = {
  'book-class': { label: 'Reservar', Icon: CalendarPlus, to: '/reservas' },
  'start-workout': { label: 'Treino', Icon: Dumbbell, to: '/meu-treino' },
  'view-pt': { label: 'Personal Training', Icon: HeartPulse, to: '/pt/sessions' },
  'connect-wearable': { label: 'Ligar o relógio', Icon: Watch, to: '/smartwatch' },
  'view-progress': { label: 'Progresso', Icon: LineChart, to: '/progress' },
  'edit-profile': { label: 'Perfil', Icon: UserIcon, to: '/profile' },
};

const fmtDay = (d) => {
  if (!d) return '';
  const dt = new Date(d); const today = new Date();
  const same = dt.toDateString() === today.toDateString();
  const t = dt.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
  return same ? `Hoje · ${t}` : `${dt.toLocaleDateString('pt-PT', { weekday: 'short', day: '2-digit', month: '2-digit' })} · ${t}`;
};

/** O treino de hoje, para o Dashboard: só se pergunta quando o ginásio tem o módulo e o perfil tem acesso; uma falha aqui nunca estraga o Início. */
function useTodayWorkout() {
  const { hasPermission } = useAuth();
  const { isEnabled } = useCapabilities();
  const allowed = isEnabled('TrainingPlans') && hasPermission('canAccessWorkoutPlans');
  const [today, setToday] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!allowed) { setToday(null); setFailed(false); return undefined; }
    let alive = true;
    workoutExecution.today(new Date().toLocaleDateString('en-CA'))
      .then((r) => { if (alive) { setToday(r.data && r.data.state !== 'NoPlan' ? r.data : null); setFailed(false); } })
      // Uma falha não pode ficar idêntica a «não tem treino hoje»: o cartão do treino desaparecia e o
      // atleta ia-se embora sem treinar. Aqui fica-se a saber que não foi possível confirmar.
      .catch(() => { if (alive) { setToday(null); setFailed(true); } });
    return () => { alive = false; };
  }, [allowed]);
  return { today, failed };
}

export default function ClientHomePage() {
  const nav = useNavigate();
  const { today: training, failed: trainingFailed } = useTodayWorkout();
  const { user, hasPermission } = useAuth();
  const { isEnabled } = useCapabilities();
  const [d, setD] = useState(null);
  const [state, setState] = useState('loading'); // loading|ok|error

  const load = () => {
    setState('loading');
    client.home().then((r) => { setD(r.data); setState('ok'); }).catch(() => setState('error'));
  };
  useEffect(load, []);

  const go = (url) => nav(legacyHomeTarget(url));

  if (state === 'loading') return <div className="gm-page"><div className="gm-skeleton" style={{ minHeight: 190 }} aria-busy="true" aria-label="A carregar" /></div>;
  if (state === 'error' || !d) {
    return (
      <div className="gm-page">
        <div className="gm-empty" role="alert">
          <strong>Não foi possível carregar o início</strong>
          Verifique a ligação e tente outra vez.
          <div><button type="button" className="gm-btn gm-btn-ghost" onClick={load}>Tentar novamente</button></div>
        </div>
      </div>
    );
  }

  const next = d.nextAction;
  // O treino de hoje é o foco do Dashboard quando não há uma reserva ou uma sessão mais urgente; senão é um cartão ao lado.
  const trainingHero = !!training && (!next || next.kind === 'workout');
  const trainingActive = training?.state === 'InProgress';
  const trainingDone = !!training?.completedTodayExecutionId && !trainingActive;
  const startTraining = () => nav('/treino', { state: { start: true, why: training?.suggestedBy ?? null } });
  const ft = d.fitnessToday;
  const goals = d.goals || [];
  const memberQuick = (d.quickActions || []).filter((k) => !(k === 'start-workout' && training)).map((k) => QUICK[k]).filter(Boolean);   // o treino de hoje já está em destaque
  // Atalhos de quem gere (treinador com permissão, ou administrador a ver o portal do sócio): só com o módulo ligado e a permissão do perfil.
  const staffQuick = [];
  if (isEnabled('TrainingPlans') && hasPermission('canManageWorkoutPlans')) staffQuick.push({ label: 'Planos dos atletas', Icon: ListChecks, to: '/admin/workout-plans-studio' });
  if (hasPermission('canManageExerciseLibrary')) staffQuick.push({ label: 'Biblioteca', Icon: Library, to: '/admin/exercise-library' });
  const quick = [...memberQuick, ...staffQuick];
  const firstName = (user?.name || '').split(' ')[0];

  return (
    <div className="gm-page home" data-testid="client-home">
      <div className="gm-grid">
        {firstName ? <p className="home-hello gm-col-12" data-testid="home-hello">Olá, {firstName}</p> : null}
        {trainingHero ? (
          <TrainingHero training={training} active={trainingActive} done={trainingDone} onStart={startTraining} onOpen={() => nav('/meu-treino')} streak={d.streak} />
        ) : next ? (
          <section className="gm-hero gm-col-8" aria-label="Próxima ação">
            <div className="gm-hero-eyebrow">{next.when ? fmtDay(next.when) : 'Quando quiser'}</div>
            <h2 className="gm-hero-title">{next.title}</h2>
            {d.streak > 0 ? <p className="gm-hero-sub"><Flame size={15} aria-hidden="true" style={{ verticalAlign: '-2px' }} /> {d.streak} {d.streak === 1 ? 'dia seguido' : 'dias seguidos'}</p> : null}
            <div className="gm-hero-row">
              <button type="button" className="gm-btn gm-btn-light" onClick={() => next.actionUrl && go(next.actionUrl)}>
                {next.kind === 'workout' ? 'Começar' : 'Ver'}
              </button>
            </div>
          </section>
        ) : (
          <section className="gm-hero is-calm gm-col-8" aria-label="Próxima ação">
            <div className="gm-hero-eyebrow">Tudo em dia</div>
            <h2 className="gm-hero-title">Sem compromissos agendados</h2>
            <p className="gm-hero-sub">{d.streak > 0 ? `${d.streak} ${d.streak === 1 ? 'dia seguido' : 'dias seguidos'} a treinar.` : 'Quando marcar uma aula ou tiver treino, aparece aqui.'}</p>
          </section>
        )}

        {trainingFailed && !trainingHero ? (
          <section className="gm-card gm-col-4" role="status" data-testid="home-training-unknown">
            <div className="gm-card-head"><h3 className="gm-card-title">Treino de hoje</h3></div>
            <p className="gm-note">Não foi possível confirmar o treino de hoje.</p>
            <button type="button" className="gm-btn gm-btn-ghost" onClick={() => nav('/meu-treino')}>Ver o treino</button>
          </section>
        ) : null}

        {quick.length > 0 && (
          <nav className="gm-card gm-col-4 home-quick" aria-label="Atalhos">
            <div className="gm-card-head"><h3 className="gm-card-title">Atalhos</h3></div>
            <div className="home-quick-grid">
              {quick.map((a) => (
                <button key={a.label} type="button" className="home-quick-btn" onClick={() => nav(a.to)}>
                  <a.Icon size={20} aria-hidden="true" />
                  <span>{a.label}</span>
                </button>
              ))}
            </div>
          </nav>
        )}

        {ft && (
          <Card title="Atividade de hoje" cols="gm-col-4">
            {!ft.connected ? (
              <>
                <p className="gm-note">{ft.needsReconnect ? 'A ligação ao seu dispositivo expirou.' : 'Ligue o seu relógio para acompanhar a atividade.'}</p>
                <button type="button" className="gm-btn gm-btn-ghost gm-btn-sm" style={{ marginTop: 12 }} onClick={() => nav('/smartwatch')}>{ft.needsReconnect ? 'Religar' : 'Ligar o relógio'}</button>
              </>
            ) : !ft.hasData ? (
              <p className="gm-note">Ainda sem dados sincronizados hoje ({ft.provider}).</p>
            ) : (
              <div className="home-metrics">
                <Metric value={ft.steps?.toLocaleString('pt-PT')} label="passos" />
                <Metric value={`${((ft.distance || 0) / 1000).toFixed(1)} km`} label="distância" />
                <Metric value={`${Math.round(ft.calories || 0)}`} label="kcal" />
              </div>
            )}
          </Card>
        )}

        {goals.length > 0 && (
          <Card title="Objetivos" cols="gm-col-4">
            {goals.map((g, i) => (
              <div key={i} style={{ marginBottom: 12 }}>
                <div className="home-goal-label">
                  <span>{g.metric === 'steps' ? 'Passos (hoje)' : g.metric === 'workouts' ? 'Treinos (semana)' : g.metric}</span>
                  <span>{g.current ?? 0}{g.target ? ` / ${Math.round(g.target)}` : ''}</span>
                </div>
                {g.target ? <Bar value={g.current || 0} max={g.target} /> : null}
              </div>
            ))}
          </Card>
        )}

        {training && !trainingHero && (
          <Card title={trainingActive ? 'Treino em curso' : 'Treino de hoje'} cols="gm-col-4" onClick={startTraining} link={trainingActive ? 'Continuar' : 'Começar'}>
            <Row label={training.dayName || 'Treino'} value={plural((training.exercises || []).length, 'exercício', 'exercícios')} />
            <Row label="Duração" value={`~${estimateMinutes(training.exercises || [])} min`} />
          </Card>
        )}

        {d.ptSummary && (
          <Card title="Personal Training" cols="gm-col-4" onClick={() => nav('/pt/sessions')} link="Abrir">
            <Row label="Sessões restantes" value={d.ptSummary.remaining ?? '—'} />
            {d.ptSummary.validUntil && <Row label="Validade" value={new Date(d.ptSummary.validUntil).toLocaleDateString('pt-PT')} />}
            <Row label="Próxima" value={d.ptSummary.nextSession ? fmtDay(d.ptSummary.nextSession.scheduledAt) : 'sem marcação'} />
          </Card>
        )}

        {(d.upcomingBookings || []).length > 0 && (
          <Card title="Próximas reservas" cols="gm-col-4" onClick={() => nav('/reservas/minhas')} link="Ver todas">
            {d.upcomingBookings.slice(0, 3).map((b, i) => (<Row key={i} label={b.name || 'Aula'} value={fmtDay(b.start)} />))}
          </Card>
        )}

        {d.progressSummary && (
          <Card title="Progresso" cols="gm-col-4" onClick={() => nav('/progress')} link="Abrir">
            <div className="home-prog">
              <span className="home-prog-values">{Number(d.progressSummary.first)} → {Number(d.progressSummary.last)}</span>
              <span className="gm-note">({Number(d.progressSummary.delta) > 0 ? '+' : ''}{Number(d.progressSummary.delta).toFixed(1)})</span>
            </div>
          </Card>
        )}

        {d.notifications && (
          <Card title="Notificações" cols="gm-col-4" onClick={() => nav('/notifications')} link="Ver todas">
            {d.notifications.unread > 0
              ? <p className="gm-note" data-testid="home-unread"><strong>{d.notifications.unread}</strong> por ler</p>
              : <p className="gm-note">Está tudo lido.</p>}
            {(d.notifications.recent || []).slice(0, 3).map((n) => (
              <Row key={n.id} label={n.title || n.message} value={new Date(n.createdAt).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' })} />
            ))}
          </Card>
        )}

        {d.membershipSummary?.avenca && (
          <Card title="A minha conta" cols="gm-col-4" onClick={() => nav('/billing')} link="Abrir">
            <Row label="Avença" value={d.membershipSummary.avenca.status} />
            {d.membershipSummary.avenca.endDate && <Row label="Validade" value={new Date(d.membershipSummary.avenca.endDate).toLocaleDateString('pt-PT')} />}
            {d.membershipSummary.debt && !d.membershipSummary.debt.regularizado && (
              <div className="gm-error" style={{ marginTop: 10 }}>Em dívida: {Number(d.membershipSummary.debt.outstanding).toFixed(2)} €</div>
            )}
          </Card>
        )}

        <InstallHint />
      </div>
    </div>
  );
}

const TrainingHero = ({ training, active, done, onStart, onOpen, streak }) => {
  const exercises = training.exercises || [];
  const sets = exercises.reduce((n, e) => n + (e.prescription?.setsTarget || 0), 0);
  return (
    <section className="gm-hero gm-col-8" aria-labelledby="home-training-title" data-testid="home-training">
      <div className="gm-hero-eyebrow">{active ? 'Treino em curso' : done ? 'Já treinou hoje' : 'O treino de hoje'}</div>
      <h2 id="home-training-title" className="gm-hero-title">{training.dayName || 'Treino'}</h2>
      <p className="gm-hero-sub">
        {training.planTitle}
        {streak > 0 ? <> · <Flame size={14} aria-hidden="true" style={{ verticalAlign: '-2px' }} /> {streak} {streak === 1 ? 'dia seguido' : 'dias seguidos'}</> : null}
      </p>
      <div className="gm-chip-row" style={{ marginBottom: 20 }}>
        <span className="gm-chip"><Clock size={14} aria-hidden="true" /> ~{estimateMinutes(exercises)} min</span>
        <span className="gm-chip"><Dumbbell size={14} aria-hidden="true" /> {plural(exercises.length, 'exercício', 'exercícios')}</span>
        <span className="gm-chip"><Layers size={14} aria-hidden="true" /> {plural(sets, 'série', 'séries')}</span>
      </div>
      <div className="gm-hero-row">
        <button type="button" className="gm-btn gm-btn-light" onClick={onStart} data-testid="home-start">
          {active ? <><RotateCcw size={18} aria-hidden="true" /> Continuar treino</> : <><Play size={18} fill="currentColor" aria-hidden="true" /> {done ? 'Treinar outra vez' : 'Começar treino'}</>}
        </button>
        <button type="button" className="gm-btn gm-btn-ghost-light" onClick={onOpen}>Ver o plano</button>
      </div>
    </section>
  );
};

const Card = ({ title, children, onClick, link, cols = 'gm-col-4' }) => (
  <section
    className={`gm-card ${cols} ${onClick ? 'is-clickable' : ''}`}
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
  >
    <div className="gm-card-head">
      <h3 className="gm-card-title">{title}</h3>
      {link ? <span className="gm-card-link" aria-hidden="true">{link} →</span> : null}
    </div>
    {children}
  </section>
);
const Row = ({ label, value }) => (<div className="home-row"><span className="gm-note">{label}</span><span className="home-row-value">{value}</span></div>);
const Metric = ({ value, label }) => (<div className="gm-stat"><span className="gm-stat-value">{value ?? '—'}</span><span className="gm-stat-label">{label}</span></div>);
const Bar = ({ value, max }) => { const pct = Math.min(100, Math.round((value / max) * 100)); return (<div className="home-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}><div style={{ width: `${pct}%` }} /></div>); };
